import {
  attachRecaptchaToken,
  checkStatus,
  createRequest,
  isKFDomain
} from "./utils.js";
import { Queue } from "./queue.js";
import { BaseService } from "./base.service.js";
import { memoryCache } from "./memory.cache.js";

// eslint-disable-next-line kf/const-naming-convention
const cacheWatcher = new Map();

const startWatcher = (key, timeout) => {
  if (cacheWatcher.has(key)) {
    return;
  }
  setTimeout(function clearSWRCache() {
    cacheWatcher.delete(key);
  }, timeout);
};

class PublicService extends BaseService {
  sendRequest(url, options) {
    let headerOptions = options;
    let _sendErrorToast = this._sendErrorToast;
    let controller = new AbortController();
    options.signal = controller.signal;

    let requestObject = async function creatingRequest() {
      if (options.key && cacheWatcher.has(options.key)) {
        return cacheWatcher.get(options.key);
      }

      const promise = new Promise(async function handler(resolve, reject) {
        if (options.key) {
          let data = memoryCache.get(options.key);
          if (data) {
            return resolve(data);
          }
        }
        if (isKFDomain(url)) {
          headerOptions = await attachRecaptchaToken(options);
        }
        let request = await createRequest(url, headerOptions);
        fetch(request)
          .then(checkStatus)
          .then(async (response) => {
            let data = await response.json();
            if (options.key) {
              memoryCache.set(options.key, data);
            }
            resolve(data);
          })
          .catch((err) => {
            _sendErrorToast(options, err);
            reject(err);
          });
      });

      if (options.key) {
        cacheWatcher.set(options.key, promise);
        startWatcher(options.key, options.staleTime);
      }

      return promise;
    };

    return [requestObject, () => controller.abort()];
  }

  swrGet(url, options) {
    options.method = "GET";
    return this.sendRequest(url, options);
  }

  postq(url, options = {}, callback = () => false, queueName = "default") {
    let queue = Queue.getQueue(queueName, this.sendRequest.bind(this));
    options.method = "POST";
    return queue.queueRequest(url, options, callback);
  }

  fetch(url, options = {}) {
    options = Object.assign({ method: "GET" }, options);
    let request = createRequest(url, options);
    return fetch(request).then(checkStatus);
  }

  async upload(url, options = {}) {
    if (isKFDomain(url)) {
      options = await attachRecaptchaToken(options);
    }
    let request = createRequest(url, options);
    return fetch(request)
      .then(checkStatus)
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  async download(url, options = {}) {
    if (isKFDomain(url)) {
      options = await attachRecaptchaToken(options);
      if (!options.headers) {
        options.headers = {};
      }
      options.headers.redirect = "follow";
    }
    let request = await createRequest(url, options);
    return fetch(request)
      .then(checkStatus)
      .then((response) => response.blob())
      .catch((err) => {
        return Promise.reject(err);
      });
  }
}

const PUBLIC_SERVICE_INSTANCE = new PublicService();
export { PUBLIC_SERVICE_INSTANCE as PublicService };
